import { z } from "zod";

/**
 * Source/transport that produced an `InboundEmail` row.
 *
 * Single source of truth: the RPC contract (`@erp/rpc`), the backend Sequelize
 * model, and the frontend `InboundEmailDTO` all derive from this schema, so
 * widening the set is a one-line change here rather than three.
 *
 * - `sendgrid-inbound-webhook` / `microsoft-graph-webhook` — top-level emails
 *   delivered to a monitored mailbox.
 * - `microsoft-graph-item-attachment` / `eml-attachment` — child emails
 *   extracted from an attachment on a forwarded message (an Outlook "forward as
 *   attachment" Graph itemAttachment, or a parsed `.eml` file). See the backend
 *   `parseAttachedEmail.ts`.
 * - `microsoft-graph-corpus` — historical emails imported from the S3 email
 *   corpus by a backfill (not delivered live). See the backend
 *   `import-corpus-emails-to-collection-threads` maintenance task.
 * - `uploaded-email` — emails parsed from a user-uploaded `.eml`/`.msg` file
 *   (e.g. dropped onto a collection thread as evidence, not delivered to a
 *   monitored mailbox). See the backend `CollectionThreadEmailUploadService`.
 */
export const InboundEmailServiceSchema = z.enum([
  "sendgrid-inbound-webhook",
  "microsoft-graph-webhook",
  "gmail-poll",
  "microsoft-graph-item-attachment",
  "eml-attachment",
  "microsoft-graph-corpus",
  "uploaded-email",
]);

export type InboundEmailService = z.infer<typeof InboundEmailServiceSchema>;
